import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { Search, CreditCard, Users, DollarSign, Eye, Edit2, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { AssinaturaDetailModal } from "@/components/admin/AssinaturaDetailModal";
import { AssinaturaEditPlanModal } from "@/components/admin/AssinaturaEditPlanModal";
import { AssinaturaCardsModal } from "@/components/admin/AssinaturaCardsModal";
import { AssinaturaCancelModal } from "@/components/admin/AssinaturaCancelModal";

interface SubscriptionWithUser {
  id: string;
  user_id: string;
  plan_code: string;
  status: string;
  price_cents: number;
  next_billing_date: string | null;
  started_at: string;
  canceled_at: string | null;
  profiles: {
    full_name: string;
    email: string;
  } | null;
}

interface Stats {
  total_subscriptions: number;
  active_subscriptions: number;
  monthly_revenue: number;
}

export default function Assinaturas() {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithUser[]>([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState<SubscriptionWithUser[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState<Stats>({
    total_subscriptions: 0,
    active_subscriptions: 0,
    monthly_revenue: 0,
  });
  
  // Modal states
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionWithUser | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditPlanModal, setShowEditPlanModal] = useState(false);
  const [showCardsModal, setShowCardsModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    loadSubscriptions();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = subscriptions.filter((sub) =>
        sub.profiles?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.plan_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSubscriptions(filtered);
    } else {
      setFilteredSubscriptions(subscriptions);
    }
  }, [searchTerm, subscriptions]);

  async function loadSubscriptions() {
    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select(`
          *,
          profiles:user_id (
            full_name
          )
        `)
        .order("started_at", { ascending: false });

      if (error) throw error;

      const subsWithProfiles = data as any as SubscriptionWithUser[];
      setSubscriptions(subsWithProfiles);
      setFilteredSubscriptions(subsWithProfiles);

      // Calcular estatísticas
      const activeCount = subsWithProfiles.filter((s) => s.status === "active").length;
      const revenue = subsWithProfiles
        .filter((s) => s.status === "active")
        .reduce((sum, s) => sum + s.price_cents, 0);

      setStats({
        total_subscriptions: subsWithProfiles.length,
        active_subscriptions: activeCount,
        monthly_revenue: revenue,
      });
    } catch (error) {
      console.error("Error loading subscriptions:", error);
      toast.error("Erro ao carregar assinaturas");
    } finally {
      setLoading(false);
    }
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { label: "Ativa", variant: "default" as const },
      canceled: { label: "Cancelada", variant: "destructive" as const },
      past_due: { label: "Pendente", variant: "destructive" as const },
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || { 
      label: status, 
      variant: "secondary" as const 
    };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Assinaturas</h1>
          <p className="text-muted-foreground">Gerencie todas as assinaturas dos usuários</p>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Assinaturas</h1>
        <p className="text-muted-foreground">Gerencie todas as assinaturas dos usuários</p>
      </div>

      {/* Estatísticas */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Assinaturas</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_subscriptions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assinaturas Ativas</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_subscriptions}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Mensal</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.monthly_revenue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou plano..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Lista de Assinaturas */}
      <Card>
        <CardHeader>
          <CardTitle>Todas as Assinaturas</CardTitle>
          <CardDescription>Lista completa de assinaturas no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredSubscriptions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma assinatura encontrada
              </div>
            ) : (
              filteredSubscriptions.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{sub.profiles?.full_name || "Nome não disponível"}</span>
                      {getStatusBadge(sub.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Plano: {sub.plan_code.toUpperCase()} • {formatPrice(sub.price_cents)}/mês
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Iniciada em: {format(new Date(sub.started_at), "dd/MM/yyyy", { locale: ptBR })}
                      {sub.next_billing_date && (
                        <> • Próxima cobrança: {format(new Date(sub.next_billing_date), "dd/MM/yyyy", { locale: ptBR })}</>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setSelectedSubscription(sub);
                      setShowDetailModal(true);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Gerenciar
                  </Button>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      {selectedSubscription && (
        <>
          <AssinaturaDetailModal
            open={showDetailModal}
            onOpenChange={setShowDetailModal}
            subscriptionId={selectedSubscription.id}
            onEditPlan={() => {
              setShowDetailModal(false);
              setShowEditPlanModal(true);
            }}
            onViewCards={() => {
              setShowDetailModal(false);
              setShowCardsModal(true);
            }}
            onCancel={() => {
              setShowDetailModal(false);
              setShowCancelModal(true);
            }}
          />

          <AssinaturaEditPlanModal
            open={showEditPlanModal}
            onOpenChange={setShowEditPlanModal}
            subscriptionId={selectedSubscription.id}
            currentPlanCode={selectedSubscription.plan_code}
            onSuccess={() => {
              setShowEditPlanModal(false);
              loadSubscriptions();
            }}
          />

          <AssinaturaCardsModal
            open={showCardsModal}
            onOpenChange={setShowCardsModal}
            userId={selectedSubscription.user_id}
          />

          <AssinaturaCancelModal
            open={showCancelModal}
            onOpenChange={setShowCancelModal}
            subscriptionId={selectedSubscription.id}
            professionalName={selectedSubscription.profiles?.full_name || "Profissional"}
            onSuccess={() => {
              setShowCancelModal(false);
              loadSubscriptions();
            }}
          />
        </>
      )}
    </div>
  );
}
